import { LegacyPortalPurchaseHistoryProductType } from "@spwn-portal/grpc";

import { PRODUCT_TYPE } from "../../constants/ProductType";
import { formatProductVariationName } from "../formatProductVariationName";
import { getReleaseDate } from "../getReleaseDate";
import { getTicketClass } from "../getTicketClass";
import { getTicketStatus } from "../getTicketStatus";

import type { ProductItem } from "../../types/ProductItem";
import type { TimeStamp } from "../../types/TimeStamp";
import type {
  LegacyPortalPurchaseHistoryOrder,
  LegacyPortalPurchaseHistoryOrderItem,
} from "@spwn-portal/grpc";
import type { useTranslation } from "react-i18next";

type Input = {
  bonusMap: Record<string, boolean> | null;
  order: LegacyPortalPurchaseHistoryOrder;
  t: ReturnType<typeof useTranslation>["t"];
};

/** 公演の開場日時（ms）を既存の日付判定が使うTimestamp形へ */
const toTimeStamp = (openTime: bigint): TimeStamp => ({
  seconds: Math.floor(Number(openTime) / 1000),
  nanoseconds: 0,
});

/** 取得元ごとの明細固有情報から、表示に使う値を取り出す */
const getItemDetail = (
  item: LegacyPortalPurchaseHistoryOrderItem
): {
  eventLongTitle: string;
  eventTitle: string;
  ticketClass: number | undefined;
  ticketId: string;
  variationName: string | undefined;
} => {
  const { detail } = item;
  if (detail.case === "spwn") {
    return {
      eventLongTitle: detail.value.eventLongTitle ?? "",
      eventTitle: detail.value.eventTitle ?? "",
      ticketClass: detail.value.class,
      ticketId: detail.value.productFirestoreId,
      variationName: detail.value.subClassName,
    };
  }
  if (detail.case === "shopify") {
    return {
      eventLongTitle: "",
      eventTitle: "",
      ticketClass: undefined,
      ticketId: "",
      variationName: detail.value.variantTitle,
    };
  }
  return {
    eventLongTitle: "",
    eventTitle: "",
    ticketClass: undefined,
    ticketId: "",
    variationName: undefined,
  };
};

/**
 * 購入履歴APIの注文明細を商品一覧の表示値へ。
 * 取得元がFirestore直読みでもAPIでも同じ商品一覧を出すため、Firestore直読み側と同じ形に揃える
 */
export const convertOrderToOrderList = (input: Input): ProductItem[] => {
  const { bonusMap, order, t } = input;

  return order.items.map((item): ProductItem => {
    const { eventLongTitle, eventTitle, ticketClass, ticketId, variationName } =
      getItemDetail(item);

    if (item.productType === LegacyPortalPurchaseHistoryProductType.TICKET) {
      const eventParts = (item.event?.parts ?? []).map((part) => ({
        name: part.name,
        openTime: toTimeStamp(part.openTime),
      }));
      const { firstPartTime, lastPartTime, status } =
        getTicketStatus(eventParts);

      return {
        eventTitle,
        productName: eventLongTitle,
        variationName: item.name,
        price: item.price,
        quantity: item.quantity,
        type: PRODUCT_TYPE.TICKET,
        imageSrc: item.event?.defaultImage ?? "",
        isImageLoading: false,
        liveTicketInfo: {
          dateInfo: getReleaseDate(firstPartTime, lastPartTime, t),
          status,
          eventId: item.eid ?? "",
          ticketId,
          class: getTicketClass(ticketClass),
          hasBonusMedia: bonusMap?.[ticketId] === true,
        },
      };
    }

    return {
      eventTitle,
      productName: eventLongTitle,
      variationName: formatProductVariationName(item.name, variationName),
      price: item.price,
      quantity: item.quantity,
      type: PRODUCT_TYPE.GOODS,
      imageSrc: item.thumbnail ?? "",
      isImageLoading: false,
    };
  });
};
